import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { getOptimizedImageUrl } from '../lib/cms';

export default function SpirituelleBoegerPage() {
  return (
    <>
      <SeoHead
        title="Spirituelle bøger og kunst – Forlaget Golden Eagle | Lone Hvid"
        description="Spirituelle bøger af Lone Hvid: 'Præstindens Vej' og 'Månetræer' samt spirituel uld-, fjer- og silkekunst på Ibjerget."
        canonicalUrl="https://www.ibjerget.dk/spirituelle-boeger"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#381d6e] text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/mock-up-af-praestindens-vej-1024x944.png', { width: 1200, quality: 75, fit: 'cover' })}
            alt="Bøger og kunst på Ibjerget"
            className="w-full h-full object-cover filter blur-sm"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/80 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-book-open" aria-hidden="true"></i>
            <span>Forlaget Golden Eagle v. Lone Hvid</span>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#faf5f9] tracking-tight leading-tight">
            Spirituelle bøger og kunst
          </h1>
          <p className="mt-6 text-base sm:text-xl text-[#f7eefa] max-w-2xl mx-auto leading-relaxed font-normal">
            Romaner, myter, naturvisdom og håndlavet kunst af forfatter og kunstner Lone Hvid.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#praestindens-vej"
              className="px-6 py-3.5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md transition-colors"
            >
              Præstindens vej
            </a>
            <a
              href="#maanetraeer"
              className="px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
            >
              Månetræer
            </a>
            <a
              href="#spirituel-kunst"
              className="px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
            >
              Spirituel kunst
            </a>
          </div>
        </div>
      </section>

      {/* BOG 1: PRÆSTINDENS VEJ */}
      <section id="praestindens-vej" className="py-20 sm:py-24 bg-[#faf5f9] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Bogcover */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative max-w-sm w-full">
                <div className="rounded-3xl overflow-hidden shadow-2xl border border-[#edd5e8] bg-white p-4">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/mock-up-af-praestindens-vej-1024x944.png', { width: 800, quality: 90, fit: 'contain' })}
                    alt="Bogcover til Præstindens vej af Lone Hvid"
                    className="w-full h-auto object-contain mx-auto rounded-xl"
                  />
                </div>
                <div className="absolute -bottom-3 right-4 bg-[#d9238c] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md">
                  Pris 250 kr.
                </div>
              </div>
            </div>

            {/* Bogtekst */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f0d9eb] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider">
                <span>Spirituel roman</span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1f142e] tracking-tight">
                Præstindens vej
              </h2>

              <p className="font-serif italic text-lg text-[#d9238c]">
                Bogen, du ikke kan undvære, hvis du vil inspireres om uglekraft og gudinden Hekate!
              </p>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Denne spirituelle roman følger Sofia fra ung gymnasiepige til præstinde for gudinden Hekate. Sofia finder en særlig energi fra fuldmånen i en udesidning, hvor hun også møder sit kraftdyr uglen, der følger hende hele livet.
                </p>
                <p>
                  Butikken Magick og dens ejer Ernst bliver omdrejningspunktet i Sofias liv. Hendes op- og nedture i livet bliver krydret med oplevelser fra tidligere liv med relation til dette liv.
                </p>
                <p>
                  Mødet med gudinden Hekate bliver vævet ind i hendes udviklingsvej. Sofia har i tidligere liv været præstinde for Hekate, og derfor har de denne særlige forbindelse, som Sofia oplever særdeles kraftfuldt.
                </p>
                <p>
                  Sofia skaber sammen med sin indianske familie en styrke, kraft og sammenhold gennem udfordringer, hvor hun undervejs får dyb visdom med sig. Præstindens vej er fyldt med modgang, overgreb, racisme og vanskelige situationer, hvor hun bliver prøvet igen og igen for at bevise, om hun er værdig til at få sin højt ønskede evne til at kunne se ind i fremtiden.
                </p>
              </div>

              {/* Faktaboks */}
              <div className="flex flex-wrap items-center gap-6 py-2 text-sm text-[#9b1b66] font-medium border-y border-[#edd5e8]">
                <span>Hardback 421 sider.</span>
                <span className="text-[#1f142e] font-bold text-base">Pris 250 kr.</span>
              </div>

              {/* Anmeldelse */}
              <div className="p-6 bg-white rounded-2xl border border-[#edd5e8] shadow-sm space-y-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#d9238c]">Anmeldelse:</p>
                <blockquote className="font-serif italic text-[#1f142e] text-base leading-relaxed">
                  "Hej Lone Ih hvor er den god din bog kan næsten ikke lægge den fra mig. Det er som at åbne en pose slik, svært at stoppe Kh. Kirsten"
                </blockquote>
              </div>

              {/* CTAs */}
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <a
                  href="https://goldeneagle.sumupstore.com/produkt/praestindens-vej-1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <span>Bestil bogen her</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
                </a>

                <Link
                  to="/spirituelle-boeger/praestindens-vej/foerste-kapitel"
                  className="px-8 py-4 rounded-full bg-white hover:bg-[#faf5f9] text-[#1f142e] font-semibold text-sm border border-[#edd5e8] shadow-sm transition-all duration-200 flex items-center gap-2"
                >
                  <span>Læs første kapitel her</span>
                  <i className="fa-solid fa-book-open text-xs text-[#d9238c]" aria-hidden="true"></i>
                </Link>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* BOG 2: MÅNETRÆER */}
      <section id="maanetraeer" className="py-20 sm:py-24 bg-white border-t border-[#edd5e8] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Bogtekst */}
            <div className="lg:col-span-7 order-2 lg:order-1 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#faf5f9] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider">
                <span>Rammefortælling & Naturvisdom</span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1f142e] tracking-tight">
                Månetræer
              </h2>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Månetræer er en rammefortælling med 15 små historier, som hver især trækker på materiale fra sagn og myter tilsat en god portion visdom. Til hvert kapitel er der en smuk finurlig illustration af Randi Freja Nikolajsen og blade, så man kan genkende træerne i naturen efterfølgende – samt at man har en historie at huske træet på.
                </p>
              </div>

              {/* Faktaboks */}
              <div className="flex flex-wrap items-center gap-6 py-2 text-sm text-[#9b1b66] font-medium border-y border-[#edd5e8]">
                <span>Bogen er hardback, 143 sider.</span>
                <span className="text-[#1f142e] font-bold text-base">Pris 200 kr.</span>
              </div>

              {/* Anmeldelse af Månetræer */}
              <div className="p-6 sm:p-8 bg-[#fcf8fb] rounded-2xl border border-[#edd5e8] shadow-sm space-y-3">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#d9238c]">
                  Anmeldelse af Månetræer:
                </p>
                <div className="prose prose-stone text-sm text-[#4e3568] leading-relaxed space-y-3 italic">
                  <p>
                    Kunne du tænke dig at komme på en anderledes skovtur?
                  </p>
                  <p>
                    En eventyrlig og spændende rejse, hvor forskellige slags ældgamle træer, de magiske månetræer, åbenbarer deres hemmeligheder og visdom for dig?
                  </p>
                  <p>
                    Så sæt dig i første omgang godt til rette med denne bog og bered dig på at blive ført vidt omkring både i tid, sted og dimensioner.
                  </p>
                  <p>
                    Her vil du bl.a. møde eksempler fra historiske mennesker til mytiske Guder og Gudinder, dyr og naturvæsner – det man normalt kalder for eventyrvæsener.
                  </p>
                  <p>
                    Jeg vil love dig, at en almindelig skovtur aldrig mere bliver det samme, efter at have fordybet sig i denne bog.
                  </p>
                  <p>
                    Faktisk vil jeg næsten vove at påstå, at selve livet ikke helt bliver det samme som før……
                  </p>
                  <p>
                    Månetræernes indsigtsfulde fortællinger åbner for muligheden for at se verden i et nyt perspektiv.
                  </p>
                  <p>
                    At se med friske øjne på ting, som vi har lært, er på en bestemt måde.
                  </p>
                  <p>
                    Bogen synes at væve en regnbuebro mellem fantasi og virkelighed, og får måske endda én til at stille spørgsmålstegn ved, hvad der egentlig er virkeligt? Måske er det vi kalder for overnaturligt mere naturligt, end vi tror, hvis vi formår at åbne os for det – og har mod til det!
                  </p>
                  <p>
                    Håber, at dette ikke skræmmer dig, men netop har vakt din nysgerrighed og dine sanser for at møde det forunderlige, der gemmer sig i altings væsen. Ellers viser bogen her på vej…..
                  </p>
                </div>
                <p className="text-xs text-[#1f142e] font-semibold pt-2 border-t border-[#edd5e8]">
                  Liv Knusen, Rudolf Steiner-pædagog
                </p>
              </div>

              {/* CTA */}
              <div className="pt-2">
                <a
                  href="https://goldeneagle.sumupstore.com/produkt/manetraeer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200 inline-flex items-center gap-2"
                >
                  <span>Bestil bogen her</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
                </a>
              </div>

            </div>

            {/* Bogcover */}
            <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-sm w-full">
                <div className="rounded-3xl overflow-hidden shadow-2xl border border-[#edd5e8] bg-white p-4">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/forside-maanetraeer-790x1024.jpg', { width: 800, quality: 90, fit: 'contain' })}
                    alt="Bogcover til Månetræer af Lone Hvid"
                    className="w-full h-auto object-contain mx-auto rounded-xl"
                  />
                </div>
                <div className="absolute -bottom-3 right-4 bg-[#d9238c] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md">
                  Pris 200 kr.
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SEKTION 3: SPIRITUEL KUNST */}
      <section id="spirituel-kunst" className="py-20 sm:py-24 bg-[#381d6e] text-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider">
              <i className="fa-solid fa-palette" aria-hidden="true"></i>
              <span>Unika værker</span>
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#faf5f9] tracking-tight">
              Spirituel kunst
            </h2>
            <p className="text-base sm:text-lg text-[#f7eefa] leading-relaxed">
              Se vores galleri på Ibjergvej 12 eller se kunsten online.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            
            {/* Kategori 1: Uld- og fjerkunst */}
            <div className="bg-[#4a2282] rounded-3xl p-8 sm:p-10 border border-[#5f2a99] flex flex-col justify-between space-y-6 group hover:border-[#d9238c] transition-colors">
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-[#d9238c]/20 text-[#f7eefa] flex items-center justify-center">
                  <i className="fa-solid fa-feather text-xl"></i>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#faf5f9]">
                  Uld- og fjerkunst
                </h3>
                <p className="text-sm text-[#f7eefa] leading-relaxed">
                  Håndlavede billeder og unika kunstværker skabt med naturlige fjer og uld, inspireret af naturvisdom og kraftdyr.
                </p>
              </div>

              <div>
                <a
                  href="https://goldeneagle.sumupstore.com/kategori/uld-and-fjer-kunst"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3.5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                >
                  <span>Se uld- og fjerkunst online</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
                </a>
              </div>
            </div>

            {/* Kategori 2: Uld- og silkekunst */}
            <div className="bg-[#4a2282] rounded-3xl p-8 sm:p-10 border border-[#5f2a99] flex flex-col justify-between space-y-6 group hover:border-[#d9238c] transition-colors">
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-[#d9238c]/20 text-[#f7eefa] flex items-center justify-center">
                  <i className="fa-solid fa-wand-magic-sparkles text-xl"></i>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#faf5f9]">
                  Uld- og silkekunst
                </h3>
                <p className="text-sm text-[#f7eefa] leading-relaxed">
                  Finurlige og organiske kunstværker i kombination af blød uld og skinnende silke med dybe naturstemninger og symbolik.
                </p>
              </div>

              <div>
                <a
                  href="https://goldeneagle.sumupstore.com/kategori/uld-and-silke-kunst"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3.5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                >
                  <span>Se uld- og silkekunst online</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
                </a>
              </div>
            </div>

          </div>

          <div className="mt-12 text-center text-xs text-[#f7eefa]">
            <p>
              Galleri med Lones værker findes på Ibjergvej 12, 5792 Årslev. Aftal tid for åbning på tlf. <a href="tel:30297902" className="underline hover:text-white">30 29 79 02</a>.
            </p>
          </div>

        </div>
      </section>
    </>
  );
}
